import type { User } from '../types';

/**
 * Kullanıcının belirli bir modül/aksiyon iznine sahip olup olmadığını döner.
 * Admin her zaman true; custom rol varsa onun permissions JSON'una bakar;
 * sabit roller için varsayılan haritaya bakar.
 */
export function hasPermission(
  user: User | null | undefined,
  module: string,
  action: string
): boolean {
  if (!user) return false;

  if (user.role === 'admin') return true;

  let granted: boolean;

  // Backend (User.php hasPermission) aynası: aktif custom rol varsa onun matrisi
  // uygulanır (is_default'a bakılmaz); yoksa sabit rol haritasına düşülür.
  if (user.custom_role && user.custom_role.is_active && user.custom_role.permissions) {
    granted = !!user.custom_role.permissions[module]?.[action];
  } else {
  const defaultPermissions: Record<string, string[]> = {
    yonetici: [
      'dashboard',
      'projects',
      'tasks',
      'files',
      'messages',
      'users',
      'reports',
      'settings',
      'calendar',
      'site_diary',
      'punch_list',
      'attendance',
      'inspections',
      'stock',
      'subcontractors',
      'voice_notes',
      'drawings',
      'rfi',
      'equipment',
      'safety',
      'tours',
      'bim',
      'checks',
      'worker_documents',
      'guarantees',
      'material_requests',
      'change_orders',
      'toolbox_talks',
      'unit_sales',
      'transmittals',
      'submittals',
      'instructions',
      'orientations',
      'copq',
      'evm',
      'agenda',
      'concrete',
      'bids',
      'expenses',
      'meetings',
      'compliance',
      'skills',
      'permits',
      'estimates',
      'erp',
      'sgk',
      'cash',
      'leaves',
    ],
    sef: [
      'dashboard',
      'projects',
      'tasks',
      'files',
      'messages',
      'calendar',
      'site_diary.view',
      'site_diary.create',
      'site_diary.update',
      'site_diary.sign',
      'site_diary.export',
      'punch_list.view',
      'punch_list.create',
      'punch_list.update',
      'punch_list.delete',
      'punch_list.assign',
      'punch_list.verify',
      'punch_list.export',
      'attendance.view',
      'attendance.create',
      'attendance.update',
      'attendance.delete',
      'attendance.export',
      'inspections.view',
      'inspections.create',
      'inspections.update',
      'inspections.delete',
      'inspections.export',
      'stock.view',
      'stock.create',
      'stock.update',
      'stock.assign',
      'subcontractors.view',
      'subcontractors.create',
      'subcontractors.update',
      'voice_notes.view',
      'voice_notes.create',
      'voice_notes.update',
      'drawings.view',
      'drawings.create',
      'drawings.update',
      'drawings.pin',
      'drawings.upload',
      'drawings.download',
      'rfi.view',
      'rfi.create',
      'rfi.update',
      'rfi.assign',
      'rfi.export',
      'equipment.view',
      'equipment.create',
      'equipment.update',
      'equipment.assign',
      'safety.view',
      'safety.create',
      'safety.update',
      'safety.assign',
      'tours.view',
      'tours.create',
      'tours.update',
      'tours.upload',
      'bim.view',
      'bim.create',
      'bim.link',
      'worker_documents.view',
      'worker_documents.create',
      'guarantees.view',
      'material_requests.view',
      'material_requests.create',
      'material_requests.approve',
      'material_requests.receive',
      'change_orders.view',
      'change_orders.create',
      'toolbox_talks.view',
      'toolbox_talks.create',
      'toolbox_talks.update',
      'toolbox_talks.sign',
      'unit_sales.view',
      'unit_sales.create',
      'unit_sales.update',
      'transmittals.view',
      'transmittals.create',
      'transmittals.update',
      'transmittals.send',
      'submittals.view',
      'submittals.create',
      'submittals.update',
      'submittals.submit',
      'instructions.view',
      'instructions.create',
      'instructions.update',
      'instructions.export',
      'orientations.view',
      'orientations.create',
      'orientations.update',
      'orientations.sign',
      'copq.view',
      'copq.update',
      'evm.view',
      'evm.update',
      'agenda',
      'concrete.view',
      'concrete.create',
      'concrete.update',
      'concrete.delete',
      'concrete.export',
      'bids.view',
      'bids.create',
      'bids.update',
      'bids.delete',
      'expenses.view',
      'expenses.create',
      'expenses.update',
      // F2B: sef eski davranisiyla hakedis/masraf ERP fis export'u yapabilir
      // (erp.view = eslestirme yonetimi admin/yonetici'de kalir, sgk YOK).
      'erp.export',
      'meetings.view',
      'meetings.create',
      'meetings.update',
      'meetings.sign',
      'compliance.view',
      'compliance.create',
      'compliance.update',
      'skills.view',
      'skills.create',
      'skills.update',
      'skills.delete',
      'permits.view',
      'permits.create',
      'permits.update',
      'permits.approve',
      'estimates.view',
      'estimates.create',
      'estimates.update',
      'leaves.view',
      'leaves.create',
    ],
    muhendis: [
      'dashboard',
      'tasks',
      'files',
      'messages',
      'calendar',
      'site_diary.view',
      'site_diary.create',
      'site_diary.export',
      'punch_list.view',
      'punch_list.create',
      'punch_list.update',
      'attendance.view',
      'attendance.export',
      'inspections.view',
      'inspections.export',
      'stock.view',
      'stock.create',
      'stock.assign',
      'subcontractors.view',
      'voice_notes.view',
      'voice_notes.create',
      'drawings.view',
      'drawings.pin',
      'drawings.download',
      'rfi.view',
      'rfi.create',
      'equipment.view',
      'equipment.assign',
      'safety.view',
      'safety.create',
      'tours.view',
      'tours.create',
      'tours.upload',
      'bim.view',
      'bim.link',
      'worker_documents.view',
      'worker_documents.create',
      'material_requests.view',
      'material_requests.create',
      'material_requests.receive',
      'change_orders.view',
      'toolbox_talks.view',
      'toolbox_talks.sign',
      'unit_sales.view',
      'transmittals.view',
      'transmittals.create',
      'submittals.view',
      'submittals.create',
      'submittals.submit',
      'instructions.view',
      'orientations.view',
      'orientations.sign',
      'copq.view',
      'evm.view',
      'agenda',
      'concrete.view',
      'concrete.create',
      'bids.view',
      'expenses.view',
      'expenses.create',
      'meetings.view',
      'meetings.create',
      'meetings.sign',
      'compliance.view',
      'compliance.create',
      'skills.view',
      'permits.view',
      'permits.create',
      'estimates.view',
      'estimates.create',
      'cash.view',
      'cash.create',
      'leaves.view',
      'leaves.create',
    ],
    kullanici: [
      'dashboard',
      'tasks.view',
      'files.view',
      'messages.view',
      'messages.send',
      'calendar.view',
      'site_diary.view',
      'punch_list.view',
      'punch_list.create',
      'attendance.view',
      'inspections.view',
      'stock.view',
      'voice_notes.view',
      'drawings.view',
      'drawings.download',
      'rfi.view',
      'equipment.view',
      'safety.view',
      'safety.create',
      'tours.view',
      'bim.view',
      'worker_documents.view',
      'worker_documents.create',
      'material_requests.view',
      'change_orders.view',
      'toolbox_talks.view',
      'toolbox_talks.sign',
      'transmittals.view',
      'submittals.view',
      'instructions.view',
      'orientations.view',
      'orientations.sign',
      'agenda',
      'concrete.view',
      'expenses.view',
      'meetings.view',
      'meetings.sign',
      'compliance.view',
      'skills.view',
      'permits.view',
      'cash.view',
      'cash.create',
      'leaves.view',
      'leaves.create',
    ],
  };

    const perms = defaultPermissions[user.role] || [];
    granted = perms.includes(module) || perms.includes(`${module}.${action}`);
  }

  // Ek izinler (users.extra_permissions): yalnız genişletme (backend aynası).
  if (!granted) {
    granted = (user.extra_permissions?.[`${module}.${action}`] ?? null) === true;
  }

  return granted;
}
